import { VirtuosoGrid } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, forwardRef } from 'react';
import { useOrganization } from '@/context/organization-context';
import { useDefaultSectionsForScope } from '@/lib/use-default-sections';
import { TemplateChatPreview } from '@/components/templates/TemplateChatPreview';
import { TemplateDocumentPreview } from '@/components/templates/TemplateDocumentPreview';
import { useTemplatePreviewContext } from '@/components/templates/useTemplatePreviewContext';
import { useEmailDocumentTemplatesControllerList } from '@/apis/backend/generated/email-document-templates/email-document-templates';
import {
  useEmailTemplateTaxonomyControllerListTags,
  useEmailTemplateTaxonomyControllerListCategories,
  useEmailTemplateTaxonomyControllerListIndustries,
} from '@/apis/backend/generated/email-template-taxonomy/email-template-taxonomy';

import {
  Clear as ClearIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {
  Box,
  Stack,
  alpha,
  Button,
  Select,
  MenuItem,
  useTheme,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  InputAdornment,
  CircularProgress,
} from '@mui/material';

// Template picker for the email-document create flow. Card grid rendering a live
// TemplateDocumentPreview per template — the same brand-filled approach
// /dashboard/templates > library uses — so the cards show real content
// instead of blank tiles when a template has no thumbnailUrl.
//
// Intentionally not reusing InlineLayoutPicker because that one lists v3
// blueprints (keyed by blueprintKey strings), not email_document_templates
// (keyed by UUID). Separate entities, separate pickers.

export interface InlineEmailDocumentTemplatePickerProps {
  selectedTemplateId: string | null;
  onSelect: (templateId: string | null) => void;
  // Height the virtualized grid scroller occupies. Defaults suit the
  // collapsible attach panel on /dashboard/emails/create; callers in a
  // modal can pass a taller value.
  scrollerMaxHeight?: number | string;
}

export function InlineEmailDocumentTemplatePicker({
  selectedTemplateId,
  onSelect,
  scrollerMaxHeight = 420,
}: InlineEmailDocumentTemplatePickerProps) {
  const { t } = useTranslation('emailGen');
  const theme = useTheme();
  // VirtuosoGrid needs a real HTMLElement to track scroll against — we own
  // an internal scrolling Box so the component works in both the inline
  // attach panel and the picker modal without relying on whatever the
  // parent does for overflow.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const clientId = activeClient?.id ?? '';
  const tenantReady = !!orgId && !!clientId;

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagId, setTagId] = useState<string | null>(null);
  const [industryId, setIndustryId] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (tagId) params.tagId = tagId;
    if (industryId) params.industryId = industryId;
    return params;
  }, [search, categoryId, tagId, industryId]);

  const { data, isLoading } = useEmailDocumentTemplatesControllerList(
    orgId,
    clientId,
    queryParams,
    {
      query: { enabled: tenantReady },
    },
  );

  const { data: categoriesData } = useEmailTemplateTaxonomyControllerListCategories(
    orgId,
    clientId,
    { query: { enabled: tenantReady } },
  );
  const { data: tagsData } = useEmailTemplateTaxonomyControllerListTags(orgId, clientId, {
    query: { enabled: tenantReady },
  });
  const { data: industriesData } = useEmailTemplateTaxonomyControllerListIndustries(
    orgId,
    clientId,
    { query: { enabled: tenantReady } },
  );

  const categories = categoriesData?.categories ?? [];
  const tags = tagsData?.tags ?? [];
  const industries = industriesData?.industries ?? [];

  const hasActiveFilters = !!search || !!categoryId || !!tagId || !!industryId;
  const clearFilters = () => {
    setSearch('');
    setCategoryId(null);
    setTagId(null);
    setIndustryId(null);
  };

  // Shared brand + media + product pool fetched once at the picker level so
  // the N rendered cards don't each fire their own set of queries.
  const previewCtx = useTemplatePreviewContext(orgId, clientId, tenantReady);
  const { defaultHeader, defaultFooter } = useDefaultSectionsForScope(orgId, clientId);

  const templates = useMemo(() => {
    const list = data?.templates ?? [];
    return [...list].sort((a, b) => {
      const aChat = a.renderMode === 'chat' ? 0 : 1;
      const bChat = b.renderMode === 'chat' ? 0 : 1;
      return aChat - bChat;
    });
  }, [data?.templates]);

  const filterBar = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
      <TextField
        size="small"
        placeholder={t('inlineEmailDocumentTemplatePicker.searchPlaceholder', 'Search templates...')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ flex: 1, minWidth: { xs: 0, sm: 220 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <Button size="small" onClick={() => setSearch('')} sx={{ minWidth: 0, p: 0.5 }}>
                <ClearIcon fontSize="small" />
              </Button>
            </InputAdornment>
          ) : null,
        }}
      />
      <FilterSelect
        label={t('inlineEmailDocumentTemplatePicker.filter.category', 'Category')}
        options={categories.map((c) => ({ id: c.id, label: c.displayName }))}
        value={categoryId}
        onChange={setCategoryId}
      />
      <FilterSelect
        label={t('inlineEmailDocumentTemplatePicker.filter.industry', 'Industry')}
        options={industries.map((i) => ({ id: i.id, label: i.displayName }))}
        value={industryId}
        onChange={setIndustryId}
      />
      <FilterSelect
        label={t('inlineEmailDocumentTemplatePicker.filter.tag', 'Tag')}
        options={tags.map((tpl) => ({ id: tpl.id, label: tpl.displayName }))}
        value={tagId}
        onChange={setTagId}
      />
      {hasActiveFilters && (
        <Button
          size="small"
          onClick={clearFilters}
          startIcon={<ClearIcon fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          {t('inlineEmailDocumentTemplatePicker.clearFilters', 'Clear')}
        </Button>
      )}
    </Box>
  );

  if (isLoading) {
    return (
      <Box>
        {filterBar}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box>
        {filterBar}
        <Stack spacing={1} sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? t('inlineEmailDocumentTemplatePicker.empty.filtered', 'No templates match the current filters.')
              : t('inlineEmailDocumentTemplatePicker.empty.noTemplates', 'No templates yet. Skip this step — our agent will compose a layout from available sections.')}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {filterBar}
      <Box
        ref={setScrollEl}
        sx={{
          maxHeight: scrollerMaxHeight,
          overflowY: 'auto',
        }}
      >
        {scrollEl && (
          <VirtuosoGrid
            customScrollParent={scrollEl}
            totalCount={templates.length}
            overscan={400}
            components={gridComponents}
            computeItemKey={(index) => templates[index].id}
            itemContent={(index) => {
              const tpl = templates[index];
              const isSelected = selectedTemplateId === tpl.id;
              return (
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(isSelected ? null : tpl.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(isSelected ? null : tpl.id);
                    }
                  }}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                    transition: 'all 0.15s',
                    width: '100%',
                    '&:hover': {
                      borderColor: isSelected
                        ? 'primary.main'
                        : alpha(theme.palette.primary.main, 0.5),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      bgcolor: 'grey.50',
                      display: 'flex',
                      justifyContent: 'center',
                      p: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {tpl.renderMode === 'chat' ? (
                      <TemplateChatPreview
                        sourceHtml={tpl.sourceHtml ?? ''}
                        brand={previewCtx.brand}
                        displayWidth={220}
                        maxDisplayHeight={320}
                      />
                    ) : (
                      <TemplateDocumentPreview
                        document={tpl.document ?? {}}
                        brand={previewCtx.brand}
                        pickRecentMedia={previewCtx.pickRecentMedia}
                        pickTransparentMedia={previewCtx.pickTransparentMedia}
                        pickProduct={previewCtx.pickProduct}
                        pickProductRich={previewCtx.pickProductRich}
                        mediaOffset={hashStringToOffset(tpl.id)}
                        displayWidth={220}
                        maxDisplayHeight={320}
                        defaultHeader={defaultHeader}
                        defaultFooter={defaultFooter}
                      />
                    )}
                  </Box>

                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                      }}
                    >
                      <CheckIcon color="primary" sx={{ fontSize: 22 }} />
                    </Box>
                  )}

                  <Box sx={{ px: 1.25, py: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: 'block' }}
                      noWrap
                    >
                      {tpl.name}
                    </Typography>
                    {tpl.categories.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {tpl.categories.map((c) => c.displayName).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        )}
      </Box>
    </Box>
  );
}

// VirtuosoGrid layout — auto-fill grid so column count adapts to whatever
// width the picker is dropped into (narrow inline panel vs. wider modal).
// The 220px floor matches the TemplateDocumentPreview displayWidth so cards
// never crush their previews.
const GridList = forwardRef<
  HTMLDivElement,
  { style?: React.CSSProperties; children?: React.ReactNode }
>(({ style, children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: 16,
      ...style,
    }}
  >
    {children}
  </div>
));
GridList.displayName = 'InlineEmailDocumentTemplatePickerGridList';

const GridItem = forwardRef<
  HTMLDivElement,
  { style?: React.CSSProperties; children?: React.ReactNode }
>(({ style, children, ...props }, ref) => (
  <div ref={ref} {...props} style={{ display: 'flex', ...style }}>
    {children}
  </div>
));
GridItem.displayName = 'InlineEmailDocumentTemplatePickerGridItem';

const gridComponents = {
  List: GridList,
  Item: GridItem,
};

function FilterSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const { t } = useTranslation('emailGen');
  if (options.length === 0) return null;
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <InputLabel id={`email-document-picker-${label}-filter-label`}>{label}</InputLabel>
      <Select
        labelId={`email-document-picker-${label}-filter-label`}
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? e.target.value : null)}
      >
        <MenuItem value="">
          <em>{t('inlineEmailDocumentTemplatePicker.filter.all', 'All')}</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id} sx={{ textTransform: 'capitalize' }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Deterministic per-card offset into the brand media pool so each card
// previews a different image — same hash function the templates library
// page uses.
function hashStringToOffset(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
